import { useMemo, useState } from 'react';
import { InfoOutline, MultiSelect, SaladIcon, StatusBar, Typography } from '@weareopenr/component-library';
import { ItemType } from 'src/constants';
import { ValidationErrorKey } from 'src/api/getValidationReport/types';
import type {
  IValidationReportItem,
  IValidationReportItemOptionGroup,
  IValidationReportPricing,
  ValidationItemType,
} from 'src/api/getValidationReport/types';
import { useValidationReport } from 'src/hooks/useValidationReport';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { ValidationReportModal } from 'src/components/ValidationReportModal';
import type { IValidationReportSection } from 'src/components/ValidationReportModal';
import styles from './ValidationReportContainer.module.scss';

const QUICK_NOTE = (
  <span>
    <strong>Quick note</strong> - This report flags missing information which may lead to a publish failure. Please
    review the relevant warnings and amend required details by deleting this schedule and recreating.
  </span>
);

const ERROR_KEY_LABELS: Record<ValidationErrorKey, string> = {
  missing_options_in_group: 'Missing Items',
  missing_option_group: 'Missing Bundle Groups',
  missing_pos_id: 'Missing POS ID',
  missing_default_price: 'Missing Default Price',
  missing_tax_rate: 'Missing Tax Rate',
  missing_description: 'Missing description',
  no_menu_image: 'Missing menu image',
  missing_items_in_section: 'Empty section',
  gap_in_schedule: 'Gap in schedule',
};

// Option groups can be assigned to more than one item, so a handful of
// errors read as plural ("(s)") in that context instead of singular.
const OPTION_GROUP_ERROR_KEY_LABELS: Partial<Record<ValidationErrorKey, string>> = {
  missing_pos_id: 'Missing POS ID(s)',
  missing_default_price: 'Missing Default Price (s)',
  missing_tax_rate: 'Missing Tax Rate (s)',
};

const ITEM_TYPE_LABELS: Record<ValidationItemType, string> = {
  [ItemType.STANDARD]: 'Standard',
  [ItemType.MODIFIER]: 'Modifier',
  [ItemType.BUNDLE]: 'Bundle',
  'NON-FOOD': 'Non-food',
  MODIFIER_GROUP: 'Modifier Group',
  BUNDLE_GROUP: 'Bundle Group',
};

const TYPE_OPTIONS = Object.entries(ITEM_TYPE_LABELS).map(([id, label], index) => ({
  id,
  label,
  display_order: index,
}));

const mapErrors = (errors: ValidationErrorKey[], isOptionGroup = false) =>
  errors.map(
    (error) => (isOptionGroup ? OPTION_GROUP_ERROR_KEY_LABELS[error] : undefined) ?? ERROR_KEY_LABELS[error] ?? error,
  );

// Only the first pricing entry is mapped for now - the report only ever
// carries one price scheme per item/option group at this point. Its errors
// are flattened directly into the section's own error list.
const flatErrors = (errors: ValidationErrorKey[], pricing: IValidationReportPricing[]): ValidationErrorKey[] => [
  ...errors,
  ...(pricing[0]?.errors ?? []),
];

const mapItemToSection = (item: IValidationReportItem): IValidationReportSection => {
  const rangeNames = item.ranges.map((range) => range.name).join(', ') || undefined;

  return {
    key: item.item_id,
    title: item.name,
    subtitle: rangeNames ? (
      <Typography size="xsmall" weight="regular" className={styles['range-subtitle']}>
        {rangeNames}
      </Typography>
    ) : undefined,
    meta: ITEM_TYPE_LABELS[item.item_type],
    errors: mapErrors(flatErrors(item.errors, item.pricing)),
  };
};

const mapOptionGroupToSection = (group: IValidationReportItemOptionGroup): IValidationReportSection => {
  const productsUsedIn = group.assigned_to.map((assignment) => assignment.name).join(', ');

  return {
    key: group.option_group_id,
    title: group.name,
    meta: ITEM_TYPE_LABELS[group.option_group_type],
    errors: mapErrors(flatErrors(group.errors, group.pricing), true),
    extra: productsUsedIn ? (
      <Typography size="xsmall" weight="regular">
        <strong>Products used in</strong> - {productsUsedIn}
      </Typography>
    ) : undefined,
  };
};

interface IContentProps {
  reportId: string;
  closeModal: () => void;
}

const Content = ({ reportId, closeModal }: IContentProps) => {
  const [selectedTypes, setSelectedTypes] = useState<ValidationItemType[]>([]);
  const [selectedErrorTypes, setSelectedErrorTypes] = useState<ValidationErrorKey[]>([]);
  const { data, isLoading, isError } = useValidationReport(reportId);

  const matchesErrorFilter = (errors: ValidationErrorKey[], pricing: IValidationReportPricing[]) =>
    selectedErrorTypes.length === 0 || selectedErrorTypes.some((e) => flatErrors(errors, pricing).includes(e));

  // Only offer error types that actually occur among the items/option groups
  // rendered here - data.error_summary covers the whole report (including
  // menu/timetable errors we don't display), which would let someone select
  // a filter that can never match anything currently on screen.
  const errorTypeOptions = useMemo(() => {
    if (!data) return [];

    const presentErrors = new Set<ValidationErrorKey>();
    (data.items ?? []).forEach((item) =>
      flatErrors(item.errors, item.pricing).forEach((error) => presentErrors.add(error)),
    );
    (data.option_groups ?? []).forEach((group) =>
      flatErrors(group.errors, group.pricing).forEach((error) => presentErrors.add(error)),
    );

    return Array.from(presentErrors).map((error, index) => ({
      id: error,
      label: ERROR_KEY_LABELS[error] ?? error,
      display_order: index,
    }));
  }, [data]);

  const sections = useMemo(() => {
    if (!data) return [];

    const items = (data.items ?? [])
      .filter(
        (item) =>
          (selectedTypes.length === 0 || selectedTypes.includes(item.item_type)) &&
          matchesErrorFilter(item.errors, item.pricing),
      )
      .map((item) => mapItemToSection(item));

    const optionGroups = (data.option_groups ?? [])
      .filter(
        (group) =>
          (selectedTypes.length === 0 || selectedTypes.includes(group.option_group_type)) &&
          matchesErrorFilter(group.errors, group.pricing),
      )
      .map((group) => mapOptionGroupToSection(group));

    return [...items, ...optionGroups];
  }, [data, selectedTypes, selectedErrorTypes]);

  return (
    <ValidationReportModal
      isOpen
      closeModal={closeModal}
      sections={sections}
      isLoading={isLoading}
      isError={isError}
      filters={
        <>
          <MultiSelect
            Icon={SaladIcon}
            label="Product Type"
            options={TYPE_OPTIONS}
            selected={selectedTypes}
            onChange={(vals) => setSelectedTypes(vals as ValidationItemType[])}
            disabled={isLoading}
          />
          <MultiSelect
            Icon={InfoOutline}
            label="Validation Type"
            options={errorTypeOptions}
            selected={selectedErrorTypes}
            onChange={(vals) => setSelectedErrorTypes(vals as ValidationErrorKey[])}
            disabled={isLoading}
          />
        </>
      }
      // This modal is now shared by menu, product, and pricing reports - the
      // "delete and recreate the schedule" note only makes sense for menu
      // publishes, so only show it when the report was generated for a menu.
      footerExtra={data?.criteria.menu_id ? <StatusBar message={QUICK_NOTE} variant="info" /> : undefined}
    />
  );
};

interface IValidationReportContainerProps {
  isOpen: boolean;
  closeModal: () => void;
  reportId: string;
}

export const ValidationReportContainer = ({ isOpen, closeModal, reportId }: IValidationReportContainerProps) => {
  const { markNotificationRead } = useUpdateNotification();

  const handleClose = () => {
    markNotificationRead(
      (notification) => notification.type === NotificationType.VALIDATION && notification.report_id === reportId,
    );
    closeModal();
  };

  return isOpen ? <Content reportId={reportId} closeModal={handleClose} /> : null;
};
